import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Register } from '../query/types'
import type { DraftCondition } from './draftCondition'
import { PropertyPicker } from './PropertyPicker'
import { ValueEditor } from './ValueEditor'
import type { FilterOptions } from './useFilterOptions'

export type { DraftCondition } from './draftCondition'

const SELECT = 'h-8 min-w-0 rounded-lg border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base'

/**
 * One condition of a group (design §5.2): the property, the operator among the property's own, the
 * value the operator takes, and a button to take the row away. Another property starts over with
 * its first operator and no value; another operator keeps the value when it takes the same kind.
 *
 * The value editor is keyed by the property, so another property also gets a fresh editor: a
 * search term typed in the list of the old one (hidden in a short list, which has no search field)
 * or the hits of an organisation search must not carry over to the new one.
 */
export function ConditionRow({
  condition,
  register,
  options,
  onChange,
  onRemove,
}: {
  condition: DraftCondition
  register: Register
  options: FilterOptions
  onChange: (next: DraftCondition) => void
  onRemove: () => void
}) {
  const property = register.properties.find((candidate) => candidate.key === condition.property)
  const operator = property?.operators.find((candidate) => candidate.key === condition.operator)

  function changeProperty(key: string) {
    if (key === condition.property) return
    const next = register.properties.find((candidate) => candidate.key === key)
    onChange({ property: key, operator: next?.operators[0]?.key ?? null })
  }

  function changeOperator(key: string) {
    const next = property?.operators.find((candidate) => candidate.key === key)
    onChange({ ...condition, operator: key, value: next && operator && next.value === operator.value ? condition.value : undefined })
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <PropertyPicker register={register} value={condition.property} onChange={changeProperty} />
        {property ? (
          <select aria-label="Operator" value={condition.operator ?? ''} onChange={(event) => changeOperator(event.target.value)} className={SELECT}>
            {property.operators.map((candidate) => (
              <option key={candidate.key} value={candidate.key}>
                {candidate.label}
              </option>
            ))}
          </select>
        ) : null}
        <Button type="button" variant="ghost" size="icon-sm" className="ml-auto" aria-label="Voorwaarde verwijderen" onClick={onRemove}>
          <X />
        </Button>
      </div>
      {property && operator ? (
        <ValueEditor
          key={property.key}
          property={property}
          operator={operator}
          value={condition.value}
          options={options}
          onChange={(value) => onChange({ ...condition, value })}
        />
      ) : null}
    </div>
  )
}
